import React, { useContext } from "react";
import * as _ from "lodash-es";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import IOrder_Item from "../../../../models/IOrder_Item";
import { NumericFormat } from "react-number-format";
import UIService from "../../../../services/UIService";
import OwTranslate from "../../../shared/OwTranslate/OwTranslate";
import { DocLogo } from "../../../shared/DocLogo/DocLogo";
import { WhiteLabelLogo } from "../../../shared/WhiteLabelLogo/WhiteLabelLogo";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import AddressDisplay from "../../../shared/AddressDisplay/AddressDisplay";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import IOrder from "../../../../models/IOrder";
import SupplierContext from "../../../../contexts/SupplierContext";
import { CatalogItemDescription } from "../../../shared/CatalogItemDescription/CatalogItemDescription";
import "./QuotePrintDetailView.scss";
import IQuoteViewOptions from "../../../../models/IQuoteViewOptions";
import { getFormattedDimensions } from "@services/OrderItemService";
import { OwBarcode } from "@components/shared/OwBarcode/OwBarcode";
import OwS3ImageViewer from "@components/shared/OwS3ImageViewer/OwS3ImageViewer";

type Props = {
    order: IOrder;
    viewOptions: IQuoteViewOptions;
};

export const QuotePrintDetailView: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);

    const vendorModel = props.order?.Vendor;
    const supplierShipTo = supplierModel.Ship_To;

    const shipmentGroupShipTo = !_.isNil(props.order.Ship_To) ? props.order.Ship_To : vendorModel?.Ship_To;
    const shipmentGroupBillTo = vendorModel?.Bill_To;

    const OrderItemsParsed = _.map(props.order.Order_Items, (item) => {
        item.item_no = Number(item.item_no);
        return item;
    });

    const OrderItems = _.sortBy(OrderItemsParsed, ["item_no", "asc"]);
    // const OrderItems = _.sortBy(props.order.Order_Items, ["item_no", "asc"]);
    const orderModel = props.order;

    const orderDate = moment(orderModel.confirmed_date || orderModel.submit_date || orderModel.created_at).format("MM/DD/YYYY");

    const orderNumsArr: string[] = [];
    const poNumsArr: string[] = [];
    const notes = orderModel.note || "";

    orderNumsArr.push(orderModel.name || "");

    if (orderModel.po_no) {
        poNumsArr.push(orderModel.po_no);
    }

    const orderNums = (
        <Link className={"no-text-style"} to={`/orders/${orderModel.guid}/entry`}>
            {orderModel.name}
        </Link>
    );
    const poNums = poNumsArr.join(",");

    const orderItemRows = _.map(OrderItems, (orderItem: IOrder_Item) => {
        const calculatedTotal = Number(orderItem.cost_price_frozen) * Number(orderItem.qty);

        const rowTotal = (
            <NumericFormat
                value={calculatedTotal}
                decimalScale={supplierModel.pricing_precision || 2}
                displayType={"text"}
                fixedDecimalScale={true}
                prefix={"$"}
            />
        );

        const pricePerUnit = (
            <NumericFormat
                value={orderItem.cost_price_frozen}
                decimalScale={supplierModel.pricing_precision || 2}
                displayType={"text"}
                fixedDecimalScale={true}
                prefix={"$"}
            />
        );

        const { widthDetail, heightDetail, depthDetail } = getFormattedDimensions(orderItem);

        return (
            <tr key={orderItem.guid}>
                <td>{orderItem.item_no}</td>
                <td>
                    {orderItem.Catalog_Item?.name}
                    {props.viewOptions.showItemPhoto ? <OwS3ImageViewer catalogItem={orderItem.Catalog_Item} /> : null}
                    {props.viewOptions.showUpc && orderItem.Catalog_Item?.upc && orderItem.Catalog_Item?.upc.length === 12 ? (
                        <div>
                            <OwBarcode format={"UPC"} value={orderItem.Catalog_Item?.upc} height={50} />
                        </div>
                    ) : null}
                    {orderItem.Order_Item_Options && orderItem.Order_Item_Options.length > 0 ? <div className="mt-2"></div> : null}
                    {orderItem.Order_Item_Options?.map((option) => {
                        return (
                            <div key={option.id}>
                                <b>{option.Item_Option?.name}:</b> {option.value}
                            </div>
                        );
                    })}
                </td>
                {props.viewOptions.showSku ? (
                    <td>
                        {orderItem.Catalog_Item?.sku}
                        {props.viewOptions.hideSkuCol && orderItem.Catalog_Item?.sku?.length ? (
                            <div>
                                <OwBarcode format={"CODE128"} value={orderItem.Catalog_Item?.sku} height={50} />
                            </div>
                        ) : null}
                    </td>
                ) : null}
                {props.viewOptions.showDescription ? (
                    <td>
                        {orderItem.description || orderItem.Catalog_Item ? (
                            <CatalogItemDescription
                                catalogItem={orderItem.Catalog_Item!}
                                orderItem={orderItem}
                                priceGroup={props.order?.Price_Group}
                            />
                        ) : (
                            ""
                        )}
                    </td>
                ) : null}
                {props.viewOptions.showUom ? <td>{orderItem.Catalog_Item?.Unit_Of_Measurement?.abbreviation}</td> : null}
                {props.viewOptions.showDimensions ? <td>{widthDetail}</td> : null}
                {props.viewOptions.showDimensions ? <td>{heightDetail}</td> : null}
                {props.viewOptions.showDimensions ? <td>{depthDetail}</td> : null}
                {props.viewOptions.showOrderQty ? (
                    <td className={"text-end"}>
                        <NumericFormat
                            value={orderItem.qty}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={supplierModel.qty_precision || 0}
                            fixedDecimalScale={true}
                        />
                    </td>
                ) : null}
                <td className={"text-end"}>{pricePerUnit}</td>
                <td className={"text-end"}>{rowTotal}</td>
            </tr>
        );
    });

    let tableClass = "table-dark";

    if (UIService.isMainViewLight()) {
        tableClass = "";
    }

    let showBillToVendorName = false;

    if (shipmentGroupBillTo?.name !== vendorModel?.name) {
        showBillToVendorName = true;
    }

    return (
        <div className={"QuotePrintDetailView"}>
            <div className={"row top-row"}>
                <div className={"col black-box " + (props.viewOptions.inlineLogoName ? "d-flex" : "")}>
                    {supplierModel.Doc_Logo ? <DocLogo maxWidth={"20rem"} height={"auto"} /> : <WhiteLabelLogo maxWidth={"20rem"} />}
                    {props.viewOptions.hideBusinessName ? null : (
                        <div className={props.viewOptions.inlineLogoName ? "ms-4" : ""}>
                            <OwTitle disableTranslation={true} title={supplierModel.name} tag={"h5"} className={"mb-0"} />
                            <div>{supplierShipTo.address_line_one}</div>
                            <div>{supplierShipTo.address_line_two}</div>
                            <div>
                                {supplierShipTo.city}, {supplierShipTo.state} {supplierShipTo.postal_code}
                            </div>
                            <div className={"mt-2"}>
                                <div className={props.viewOptions.showPhone && supplierModel.phone ? "" : "d-none"}>
                                    <b>
                                        <OwTranslate defaultMessage={"phone"} id={"phone"} />:
                                    </b>{" "}
                                    {supplierModel.phone}
                                </div>
                                <div className={props.viewOptions.showEmail && supplierModel.email ? "" : "d-none"}>
                                    <b>
                                        <OwTranslate defaultMessage={"email"} id={"email"} />:
                                    </b>{" "}
                                    {supplierModel.email}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className={"col text-end flex-column"}>
                    <h3>
                        <OwTranslate defaultMessage={"quote"} id={"quote"} />
                    </h3>
                    <p>Date: {moment().format("MM/DD/YYYY")}</p>
                </div>
            </div>

            <div className={"row bill-to-ship-to-row mt-5"}>
                {props.viewOptions.showBillTo ? (
                    <div className={"col black-box"}>
                        <OwTitle leftIconName={"fal fa-building"} className={"underline"} title={"bill_to"} tag={"h5"} />
                        {shipmentGroupBillTo?.company_name ? null : showBillToVendorName ? (
                            <OwTitle tag={"h5"} disableTranslation={true} title={vendorModel?.name || ""} className={"mb-0"} />
                        ) : null}
                        <AddressDisplay
                            address={shipmentGroupBillTo}
                            showName={true}
                            showCompanyName={true}
                            showPhone={props.viewOptions.showBillToPhone}
                            hideEmail={true}
                            hideFax={true}
                            hideMobile={true}
                        />
                    </div>
                ) : null}

                <div className={"col black-box " + (props.viewOptions.showBillTo ? "border-left-0" : "")}>
                    <OwTitle leftIconName={"fal fa-truck"} title={"ship_to"} tag={"h5"} className={"underline"} />
                    <AddressDisplay
                        address={shipmentGroupShipTo}
                        showName={true}
                        showCompanyName={true}
                        showPhone={props.viewOptions.showShipToPhone}
                        hideEmail={true}
                        hideFax={true}
                        hideMobile={true}
                    />
                </div>
            </div>

            <div className={"mt-2"}>
                <table className={"table table-bordered " + tableClass}>
                    <tbody>
                        <tr>
                            <th>
                                <OwLabel title={"quote_date"} className={"mb-0"} />
                            </th>
                            <th>
                                <OwLabel title={"quote_no"} className={"mb-0"} />
                            </th>
                            <th>
                                <OwLabel title={"po_no"} className={"mb-0"} />
                            </th>
                            <th>
                                <OwLabel title={"notes"} className={"mb-0"} />
                            </th>
                            {props.viewOptions.showOrderDescription ? (
                                <th>
                                    <OwLabel title={"description"} />
                                </th>
                            ) : null}
                            {props.viewOptions.showOrderUser ? (
                                <th>
                                    <OwLabel title={"user"} />
                                </th>
                            ) : null}
                        </tr>
                        <tr>
                            <td>{orderDate}</td>
                            <td>{orderNums}</td>
                            <td>{poNums}</td>
                            <td>{notes}</td>
                            {props.viewOptions.showOrderDescription ? <td>{props.order.description}</td> : null}
                            {props.viewOptions.showOrderUser ? <td>{props.order.User?.name}</td> : null}
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className={""}>
                <table className={"table table-bordered " + tableClass}>
                    <tbody>
                        <tr>
                            <th>
                                <OwLabel title={"no"} className={"mb-0"} />
                            </th>
                            <th>
                                <OwLabel title={"item"} className={"mb-0"} />
                            </th>
                            {props.viewOptions.showSku ? (
                                <th>
                                    <OwLabel title={"sku"} className={"mb-0"} />
                                </th>
                            ) : null}
                            {props.viewOptions.showDescription ? (
                                <th>
                                    <OwLabel title={"description"} className={"mb-0"} />
                                </th>
                            ) : null}
                            {props.viewOptions.showUom ? (
                                <th>
                                    <OwLabel title={"uom"} className={"mb-0"} />
                                </th>
                            ) : null}
                            {props.viewOptions.showDimensions ? (
                                <>
                                    <th>
                                        <OwLabel title={"W"} />
                                    </th>
                                    <th>
                                        <OwLabel title={"H"} />
                                    </th>
                                    <th>
                                        <OwLabel title={supplierModel.length_for_depth ? "L" : "D"} />
                                    </th>
                                </>
                            ) : null}
                            {props.viewOptions.showOrderQty ? (
                                <th className={"text-end"}>
                                    <OwLabel title={"qty"} className={"mb-0"} />
                                </th>
                            ) : null}
                            <th className={"text-end"}>
                                <OwLabel title={"unit_price"} className={"mb-0"} />
                            </th>
                            <th className={"text-end"}>
                                <OwLabel title={"total"} className={"mb-0"} />
                            </th>
                        </tr>
                        {orderItemRows}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
